import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import DefinitionSection from '../FirstAid/components/DefinitionSection';
import ProcedureSection from '../FirstAid/components/ProcedureSection';
import Quiz from '../FirstAid/components/Quiz';
import '../FirstAid/firstaid.css';
import QuizMultipleChoice from '../FirstAid/components/QuizMultipleChoice';
import Results from '../FirstAid/components/Results';
import { SIGNSANDSYMPTOMS } from './signsSymptomsData';
import Button from '../../components/Button/Button';

export default function Child({ signAndSymptomsId }) {
  const [step, setStep] = useState(1);
  const [choice, setChoice] = useState('');
  const [answeredQuestion, setAnsweredQuestion] = useState(null);
  const data = SIGNSANDSYMPTOMS.find((aid) => aid.id === signAndSymptomsId);
  const totalSteps = 6;
  const progress = (step / totalSteps) * 100;
  const navigation = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    const savedProgress = JSON.parse(localStorage.getItem('childSignsAndSymptoms') || '{}');
    const currentAid = savedProgress[signAndSymptomsId];
    if (currentAid?.step) {
      setStep(currentAid.step);
    }
  }, [signAndSymptomsId]);

  useEffect(() => {
    const stored = localStorage.getItem('childSignsAndSymptoms');
    let savedProgress = {};

    try {
      savedProgress = stored ? JSON.parse(stored) : {};
    } catch {
      savedProgress = {};
    }

    const updatedProgress = {
      ...savedProgress,
      [signAndSymptomsId]: {
        step,
        progress,
      },
    };

    localStorage.setItem('childSignsAndSymptoms', JSON.stringify(updatedProgress));
  }, [step, signAndSymptomsId, progress]);

  const closePage = () => {
    navigation(-1);
  };

  const resetProgress = () => {
    setStep(1);
    setChoice('');
    setAnsweredQuestion(null);
  };

  const renderContent = (currentStep) => {
    switch (currentStep) {
      case 1:
        return (
          <DefinitionSection
            symptoms
            question={data.steps.intro.text}
            definition={data.steps.intro.definition}
            bannerImage={data.image}
          />
        );
      case 2:
        return (
          <ProcedureSection
            symptoms
            question={data.steps.whatHelps.question}
            options={data.steps.whatHelps.options}
            stepcolors={data.steps.whatHelps.stepcolors}
          />
        );
      case 3:
        return (
          <ProcedureSection
            symptoms
            question={data.steps.whenToSay.question}
            options={data.steps.whenToSay.options}
            stepcolors={data.steps.whenToSay.stepcolors}
          />
        );
      case 4:
        return (
          <QuizMultipleChoice
            symptoms
            introduction={data.steps.quizMCQ1.intro}
            question={data.steps.quizMCQ1.question}
            options={data.steps.quizMCQ1.options}
            stepcolors={data.steps.quizMCQ1.stepcolors}
            choice={choice}
            choices={data.steps.quizBoolean.options}
            setChoice={setChoice}
            answer={data.steps.quizMCQ1.answer}
            setAnsweredQuestion={setAnsweredQuestion}
          />
        );
      case 5:
        return (
          <QuizMultipleChoice
            symptoms
            introduction={data.steps.quizMCQ2.intro}
            question={data.steps.quizMCQ2.question}
            options={data.steps.quizMCQ2.options}
            choices={data.steps.quizBoolean.options}
            stepcolors={data.steps.quizMCQ2.stepcolors}
            choice={choice}
            setChoice={setChoice}
            answer={data.steps.quizMCQ2.answer}
            setAnsweredQuestion={setAnsweredQuestion}
          />
        );
      case 6:
        return (
          <Quiz
            introduction={data.steps.quizBoolean.intro}
            question={data.steps.quizBoolean.question}
            options={data.steps.quizBoolean.options}
            stepcolors={data.steps.quizBoolean.stepcolors}
            choice={choice}
            answer={data.steps.quizBoolean.answer}
            setChoice={setChoice}
            setAnsweredQuestion={setAnsweredQuestion}
          />
        );
      default:
        return null;
    }
  };

  const triggerNextStep = (currentStep) => {
    if (currentStep < totalSteps) {
      setStep(currentStep + 1);
    } else {
      setChoice('Finish');
      setStep(1);
    }
  };

  const triggerBackStep = () => {
    setStep(step - 1);
  };

  if (choice) {
    return (
      <div className="firstaid__container">
        <div className="content">
          <div key={`${choice}-${step}`} className="fade-in">
            <Results
              totalSteps={totalSteps}
              category={data.name}
              choice={choice}
              step={step}
              setStep={setStep}
              setChoice={setChoice}
              options={data.steps.quizBoolean.options}
              answeredQuestion={answeredQuestion}
              setAnsweredQuestion={setAnsweredQuestion}
              resetProgress={resetProgress}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="firstaid__container">
      <div className="firstaid__progress">
        <ProgressBar
          width={100}
          percent={progress}
          firstAid
          onClose={closePage}
          resetProgressIcon
          resetProgress={resetProgress}
        />
      </div>
      <div className="content">
        {choice === '' && (
          <div key={step} className="content__container fade-in">
            {renderContent(step)}
          </div>
        )}
        {choice === '' && (
          <div className="symptoms__footer">
            {step > 1 && (
              <Button hierarchy="secondary" width="defaultWidth" onClick={triggerBackStep}>
                {t('firstAid.backButton')}
              </Button>
            )}
            <div style={{ marginLeft: 'auto' }}>
              {![4, 5, 6].includes(step) && (
                <Button hierarchy="primary" width="defaultWidth" onClick={() => triggerNextStep(step)}>
                  {t('firstAid.nextButton')}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
